<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

// Ordered list of fee rules. The FIRST rule whose conditions match applies its effect, then the
// engine stops — so place the most specific (e.g. waivers) first. Each rule:
// { label, match: 'all'|'any', conditions: [{ attribute, operator, value }], effect: { kind, value } }
const props = defineProps({
  modelValue: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const OPERATORS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'truthy', 'falsy'];
const VALUELESS_OPERATORS = ['truthy', 'falsy'];
const EFFECT_KINDS = [
  'waive',
  'discount_percent',
  'discount_amount',
  'surcharge_percent',
  'surcharge_amount',
];

const rules = computed({
  get: () => props.modelValue || [],
  set: value => emit('update:modelValue', value),
});

function updateRule(index, patch) {
  rules.value = rules.value.map((rule, idx) =>
    idx === index ? { ...rule, ...patch } : rule
  );
}

function addRule() {
  rules.value = [
    ...rules.value,
    { label: '', match: 'all', conditions: [], effect: { kind: 'waive' } },
  ];
}

function removeRule(index) {
  rules.value = rules.value.filter((_, idx) => idx !== index);
}

function moveRule(index, direction) {
  const target = index + direction;
  if (target < 0 || target >= rules.value.length) return;
  const next = [...rules.value];
  [next[index], next[target]] = [next[target], next[index]];
  rules.value = next;
}

// --- Conditions within a rule ---
function conditionsOf(rule) {
  return Array.isArray(rule.conditions) ? rule.conditions : [];
}

function updateConditions(index, conditions) {
  updateRule(index, { conditions });
}

function addCondition(index) {
  updateConditions(index, [
    ...conditionsOf(rules.value[index]),
    { attribute: '', operator: 'eq', value: '' },
  ]);
}

function updateCondition(ruleIndex, condIndex, patch) {
  const conditions = conditionsOf(rules.value[ruleIndex]).map((cond, idx) =>
    idx === condIndex ? { ...cond, ...patch } : cond
  );
  updateConditions(ruleIndex, conditions);
}

function removeCondition(ruleIndex, condIndex) {
  updateConditions(
    ruleIndex,
    conditionsOf(rules.value[ruleIndex]).filter((_, idx) => idx !== condIndex)
  );
}

// --- Effect ---
function updateEffect(index, patch) {
  updateRule(index, { effect: { ...(rules.value[index].effect || {}), ...patch } });
}

function effectKind(rule) {
  return rule.effect?.kind || 'waive';
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <p v-if="!rules.length" class="py-2 text-xs text-n-slate-10">
      {{ t('TRAMITE_TYPES.PAYMENT.RULES.EMPTY') }}
    </p>

    <div
      v-for="(rule, index) in rules"
      :key="index"
      class="flex flex-col gap-3 p-3 rounded-lg border border-n-weak"
    >
      <!-- Rule header: label + reorder + remove -->
      <div class="flex gap-2 items-center">
        <input
          :value="rule.label"
          type="text"
          :placeholder="t('TRAMITE_TYPES.PAYMENT.RULES.LABEL_PLACEHOLDER')"
          class="flex-1 px-2 h-8 text-sm font-medium rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
          @input="updateRule(index, { label: $event.target.value })"
        />
        <Button
          v-tooltip.top="t('TRAMITE_TYPES.PAYMENT.RULES.MOVE_UP')"
          variant="ghost"
          color="slate"
          size="xs"
          icon="i-lucide-chevron-up"
          :disabled="index === 0"
          @click="moveRule(index, -1)"
        />
        <Button
          v-tooltip.top="t('TRAMITE_TYPES.PAYMENT.RULES.MOVE_DOWN')"
          variant="ghost"
          color="slate"
          size="xs"
          icon="i-lucide-chevron-down"
          :disabled="index === rules.length - 1"
          @click="moveRule(index, 1)"
        />
        <Button
          variant="ghost"
          color="ruby"
          size="xs"
          icon="i-lucide-trash-2"
          @click="removeRule(index)"
        />
      </div>

      <!-- Conditions -->
      <div class="flex flex-col gap-2 pl-1">
        <div class="flex gap-2 items-center text-xs text-n-slate-10">
          <span>{{ t('TRAMITE_TYPES.PAYMENT.RULES.WHEN') }}</span>
          <select
            :value="rule.match || 'all'"
            class="px-1.5 h-6 text-xs rounded border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
            @change="updateRule(index, { match: $event.target.value })"
          >
            <option value="all">{{ t('TRAMITE_TYPES.PAYMENT.RULES.MATCH_ALL') }}</option>
            <option value="any">{{ t('TRAMITE_TYPES.PAYMENT.RULES.MATCH_ANY') }}</option>
          </select>
        </div>

        <div
          v-for="(cond, condIndex) in conditionsOf(rule)"
          :key="condIndex"
          class="flex flex-wrap gap-2 items-center"
        >
          <input
            :value="cond.attribute"
            type="text"
            :placeholder="t('TRAMITE_TYPES.PAYMENT.RULES.ATTRIBUTE_PLACEHOLDER')"
            class="px-2 h-8 font-mono text-sm rounded-md border w-40 border-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
            @input="updateCondition(index, condIndex, { attribute: $event.target.value })"
          />
          <select
            :value="cond.operator || 'eq'"
            class="px-1.5 h-8 text-sm rounded-md border w-32 border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
            @change="updateCondition(index, condIndex, { operator: $event.target.value })"
          >
            <option v-for="op in OPERATORS" :key="op" :value="op">
              {{ t(`TRAMITE_TYPES.PAYMENT.OPERATORS.${op}`) }}
            </option>
          </select>
          <input
            v-if="!VALUELESS_OPERATORS.includes(cond.operator)"
            :value="cond.value"
            type="text"
            :placeholder="t('TRAMITE_TYPES.PAYMENT.RULES.VALUE_PLACEHOLDER')"
            class="flex-1 px-2 h-8 text-sm rounded-md border min-w-24 border-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
            @input="updateCondition(index, condIndex, { value: $event.target.value })"
          />
          <Button
            variant="ghost"
            color="ruby"
            size="xs"
            icon="i-lucide-x"
            @click="removeCondition(index, condIndex)"
          />
        </div>

        <div>
          <Button
            type="button"
            variant="ghost"
            color="slate"
            size="xs"
            icon="i-lucide-plus"
            :label="t('TRAMITE_TYPES.PAYMENT.RULES.ADD_CONDITION')"
            @click="addCondition(index)"
          />
        </div>
      </div>

      <!-- Effect -->
      <div class="flex flex-wrap gap-2 items-center pt-2 pl-1 border-t border-n-weak/60">
        <span class="text-xs text-n-slate-10">
          {{ t('TRAMITE_TYPES.PAYMENT.RULES.THEN') }}
        </span>
        <select
          :value="effectKind(rule)"
          class="px-1.5 h-8 text-sm rounded-md border w-48 border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
          @change="updateEffect(index, { kind: $event.target.value })"
        >
          <option v-for="kind in EFFECT_KINDS" :key="kind" :value="kind">
            {{ t(`TRAMITE_TYPES.PAYMENT.EFFECTS.${kind}`) }}
          </option>
        </select>
        <input
          v-if="effectKind(rule) !== 'waive'"
          :value="rule.effect?.value"
          type="number"
          min="0"
          step="0.01"
          class="px-2 h-8 text-sm rounded-md border w-28 border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
          @input="updateEffect(index, { value: $event.target.value === '' ? null : Number($event.target.value) })"
        />
      </div>
    </div>

    <div>
      <Button
        type="button"
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-plus"
        :label="t('TRAMITE_TYPES.PAYMENT.RULES.ADD')"
        @click="addRule"
      />
    </div>
  </div>
</template>
